import { FormEvent, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { friendlyError } from '@/lib/errors';

/**
 * Step 1 of password reset: collect email + ask the backend to send a code.
 *
 * The backend always returns 200 regardless of whether the email is registered
 * (anti-enumeration). We always show a generic "check your inbox" message and
 * link to the reset page where the user pastes the code from the email.
 */
export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await api.passwordResetRequest(email);
      setSent(true);
    } catch (err) {
      setError(friendlyError(err, 'ForgotPassword'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main style={{ maxWidth: 440, margin: '0 auto', padding: '80px 24px' }}>
      <Link to="/auth/signin" style={{ color: 'var(--ink-3)', fontSize: 13 }}>← back</Link>
      <div
        className="mono"
        style={{ fontSize: 11, color: 'var(--coral)', letterSpacing: 2, textTransform: 'uppercase', marginTop: 32, marginBottom: 14 }}
      >
        Reset password
      </div>
      <h1
        className="serif"
        style={{ fontSize: 36, lineHeight: 1.05, letterSpacing: -0.8, fontWeight: 500, marginBottom: 8 }}
      >
        Forgot your <em>password</em>?
      </h1>
      <p style={{ color: 'var(--ink-3)', fontSize: 14, marginBottom: 28 }}>
        We'll email you a code to reset it.
      </p>

      {sent ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <div style={successBox}>
            If <strong>{email}</strong> is registered, we've sent a reset code. Check your inbox (and spam folder).
          </div>
          <Link to="/auth/reset" style={btnPrimaryAsLink}>
            I have a code →
          </Link>
        </div>
      ) : (
        <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <input
            type="email"
            autoComplete="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            style={input}
          />
          {error && <ErrorBox>{error}</ErrorBox>}
          <button type="submit" disabled={submitting} style={btnPrimary}>
            {submitting ? 'Sending…' : 'Send reset code'}
          </button>
        </form>
      )}

      <p style={{ textAlign: 'center', fontSize: 13, color: 'var(--ink-3)', marginTop: 24 }}>
        Remembered it? <Link to="/auth/signin">Sign in →</Link>
      </p>
    </main>
  );
}

function ErrorBox({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ background: 'rgba(208,64,48,0.08)', color: '#a8331f', borderRadius: 12, padding: '10px 14px', fontSize: 13 }}>
      {children}
    </div>
  );
}

const successBox: React.CSSProperties = {
  background: 'rgba(56,142,60,0.08)',
  border: '1px solid rgba(56,142,60,0.2)',
  color: '#2e7d32',
  borderRadius: 12,
  padding: '14px 16px',
  fontSize: 13 };

const input: React.CSSProperties = {
  width: '100%',
  background: 'var(--surface)',
  border: '1.5px solid transparent',
  borderRadius: 14,
  padding: '14px 16px',
  fontFamily: 'var(--font-ui)',
  fontSize: 15,
  color: 'var(--ink)',
  outline: 'none' };

const btnPrimary: React.CSSProperties = {
  background: 'var(--coral)',
  color: '#fff',
  padding: '16px 24px',
  borderRadius: 100,
  fontWeight: 600,
  fontSize: 15,
  boxShadow: 'var(--shadow-card)',
  border: 'none',
  cursor: 'pointer' };

const btnPrimaryAsLink: React.CSSProperties = {
  ...btnPrimary,
  textAlign: 'center',
  textDecoration: 'none',
  display: 'block' };
